<template>
    <page-container>
        <page-header>
            Contact Imports

            <template v-slot:actions>
                <vx-button
                    @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-dark"
                    size="lg"
                >
                    <span class="vx-hidden sm:vx-inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon
                        class="vx-hidden sm:vx-inline-block"
                        :icon="faQuestionCircle"
                    ></font-awesome-icon>
                    <font-awesome-icon
                        class="vx-inline-block sm:vx-hidden"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                    ></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <contacts-import-overview
            v-show="showOverview"
            :class="{ 'vx-mb-4': !loading && list.length, 'vx-mb-16': !loading && !list.length }"
        ></contacts-import-overview>

        <page-controls v-if="list.length" class="vx-items-center">
            <template v-slot:left>
                <vx-alert v-if="recentlyCreated" color="success" :closeable="false" shadow>
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faCircleCheck"></font-awesome-icon>
                    </template>
                    <strong>New Import</strong> started successfully
                </vx-alert>
            </template>

            <template v-slot:right>
                <vx-button size="lg" block href="/contacts/import/new">
                    New Import <font-awesome-icon :icon="faPlusCircle" />
                </vx-button>
            </template>
        </page-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <vx-box
            v-if="!loading && !list.length"
            color="info"
            class="vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500"
        >
            No imports yet.
            <vx-button href="/contacts/import/new"> New Import <font-awesome-icon :icon="faPlusCircle" /> </vx-button>
        </vx-box>

        <vx-table v-if="list.length" class="lg:vx-grid-cols-7-auto vx-mt-4" rowBreakpoint="lg">
            <vx-table-row isHeader>
                <div>Status</div>
                <div class="xl:vx-col-span-2">Name</div>
                <div>Rows</div>
                <div>Errors</div>
                <div>Created at</div>
            </vx-table-row>

            <contacts-import-row
                v-for="(contactImport, index) in list"
                :modelValue="list[index]"
                :key="list[index].import_id"
                class="vx-min-h-[74px]"
            ></contacts-import-row>
        </vx-table>

        <div v-if="fetchParams.cursor" class="vx-mt-8">
            <vx-button
                @click="loadData()"
                color="muted-dark"
                size="lg"
                class="vx-grow-0"
                data-test="load-more-btn"
                :loading="loading"
            >
                Load More
                <font-awesome-icon :icon="faSpinner"></font-awesome-icon>
            </vx-button>
        </div>
    </page-container>
</template>

<script setup>
import {
    faCircleCheck,
    faCircleChevronDown,
    faCircleChevronUp,
    faPlusCircle,
    faQuestionCircle,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { VxAlert, VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageHeader from '~/components/general/PageHeader.vue';
import PageContainer from '~/components/general/PageContainer.vue';
import { computed, onMounted, ref } from 'vue';
import ContactsImportOverview from '~/components/contacts/import/ContactsImportOverview.vue';
import PageControls from '~/components/general/PageControls.vue';
import ContactsImportRow from '~/components/contacts/import/ContactsImportRow.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import importsClient from '~/services/importsClient';
import { groupImportsClient } from '~/services/index';
import PageLoading from '~/components/general/PageLoading.vue';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { useQueryParams } from '~/composables/useQueryString';

const showOverview = ref(true);

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const { onCorporate, getCurrentGroupId } = useCurrentGroup();

const defaultFilters = () => ({
    cursor: null,
    forward: false,
});

const queryParams = useQueryParams();

onMounted(async () => {
    fetchParams.value = defaultFilters();
    await loadData();

    if (queryParams.success && queryParams.import_id && queryParams.file_name) {
        if (!list.value.find((item) => item.import_id === queryParams.import_id)) {
            list.value.unshift({
                object: 'import',
                import_type: 'contact',
                import_id: queryParams.import_id,
                file_name: queryParams.file_name,
                created_at: toUTCDateTimeFormat(dayjs().utc()),
            });

            list.value = list.value.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }

        queryParams.success = undefined;
        queryParams.import_id = undefined;
        queryParams.file_name = undefined;
    }
});

const recentlyCreated = computed(() =>
    list.value[0]?.created_at
        ? dayjs.utc(list.value[0].created_at).isAfter(dayjs().utc().subtract(15, 'seconds'))
        : false
);

const loadData = () => {
    const request = onCorporate()
        ? importsClient.list(Spark.state.currentTeam.id, 'contact', { ...fetchParams.value })
        : groupImportsClient.list(Spark.state.currentTeam.id, getCurrentGroupId(), 'contact', { ...fetchParams.value });

    return fetchRecords(request);
};
</script>
